













































import { Vue, Component, Watch } from "vue-property-decorator";
import store from "@/store";
import dateHelper from "@/Scripts/utilities/date-helper";
//  types
import { DataQualityHeader } from "@/types/dataQuality/dataQualityTable";
//  components
import DataAvailabilityModal from "@/components/dataQuality/dataAvailabilityModal/DataAvailabilityModal.vue";
import DataAvailabilityTable from "@/components/dataQuality/DataAvailabilityTable/index.vue";
import AvailabilityFeaturesModal from "@/components/dataQuality/AvailabilityFeaturesModal.vue";
//  modules
import { getModule } from "vuex-module-decorators";
import VesselsModule from "@/store/clients/Vessels.module";
import DataQualityModule from "@/store/clients/DataQuality.module";
import UserModule from "@/store/clients/User.module";
import outageHubConnection from "@/utilities/signalr/signalr-outagehub-service";
import { HubConnectionState } from "@microsoft/signalr";

const Vessels = getModule(VesselsModule, store);
const DataQuality = getModule(DataQualityModule, store);
const User = getModule(UserModule, store);

@Component({
  components: {
    DataAvailabilityTable,
    DataAvailabilityModal,
    AvailabilityFeaturesModal,
  },
})
export default class DataAvailability extends Vue {
  dialog = false;
  configureFeaturesModal = false;
  searchQuery = "";
  lastUpdatedTimestamp = " ";
  isSkeletonLoading = true;
  skeletonTimeout: number | null = null;
  updateDebounceTimer: number | null = null;

  headers: DataQualityHeader[] = [];

  private readonly baseHeaders: DataQualityHeader[] = [
    {
      text: "Vessel",
      type: "string",
      value: "vesselName",
      width: "260px",
      sortable: true,
      align: "left",
    },
    {
      text: "Overall Status",
      type: "dataIndicator",
      value: "overallStatus",
      sortable: false,
      tooltip: "Indicates overall data availability status of the vessel and is determined by the most critical status.",
      align: "left",
    },
  ];

  get userCompanyId(): number | null {
    return User.userCompanyId;
  }

  // SignalR methods
  private async waitUntilConnected() {
    if (outageHubConnection.state === HubConnectionState.Connected) return;
    if (outageHubConnection.state === HubConnectionState.Disconnected) {
      await outageHubConnection.start();
      return;
    }
    await new Promise<void>(resolve => {
      const t = setInterval(() => {
        if (outageHubConnection.state === HubConnectionState.Connected) {
          clearInterval(t);
          resolve();
        }
      }, 50);
    });
  }

  private onOutageUpdated = (outage: any, vesselInfo: any) => {
    this.updateOldestDataTimestamp(vesselInfo.vesselName, outage.featureName, outage);
  };

  async created(): Promise<void> {
    await Promise.all([Vessels.refreshExtendedVessels(), DataQuality.refreshDataAvailability()]);

    // Setup SignalR connection
    outageHubConnection.off("OutageUpdated", this.onOutageUpdated);
    outageHubConnection.on("OutageUpdated", this.onOutageUpdated);

    if (this.userCompanyId !== null && this.userCompanyId !== undefined) {
      try {
        await this.waitUntilConnected();
        await outageHubConnection.invoke("JoinGroup", `company-${this.userCompanyId}`);
      } catch (e) {
        console.error("Failed to join SignalR group:", e);
      }
    }

    this.buildHeaders();

    this.lastUpdatedTimestamp = this.oldestDataTimestamp() ?? "";
    this.isSkeletonLoading = false;
  }

  beforeDestroy() {
    outageHubConnection.off("OutageUpdated", this.onOutageUpdated);
    if (this.userCompanyId !== null && this.userCompanyId !== undefined) {
      outageHubConnection.invoke("LeaveGroup", `company-${this.userCompanyId}`).catch(() => {});
    }
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }
    if (this.skeletonTimeout) {
      clearTimeout(this.skeletonTimeout);
    }
  }

  get indicatorsWithNAStatusForEachVessel(): string[] {
    const allIndicators = DataQuality.dataAvailability.reduce((acc: any, item) => {
      item.data.forEach(indicator => {
        if (!acc[indicator.featureName]) {
          acc[indicator.featureName] = [];
        }
        acc[indicator.featureName].push(indicator.outageState);
      });
      return acc;
    }, {});

    return Object.keys(allIndicators).filter(key => allIndicators[key].every((value: any) => value === 0));
  }

  oldestDataTimestamp(): string | null {
    if (DataQuality.dataAvailability.length === 0) return null;

    const timestamps = DataQuality.dataAvailability.flatMap(item => item.data.map(indicator => indicator.outageDetectionTime)).filter(timestamp => timestamp !== null) as string[];

    if (timestamps.length === 0) return null;

    const oldestTimestamp = timestamps.reduce((oldest, current) => (new Date(current) < new Date(oldest) ? current : oldest));

    return this.getFormatedDateTimeString(oldestTimestamp);
  }

  get dataAvailability(): any {
    var dataAvailability = DataQuality.dataAvailability.map(item => {
      const indicators = item.data.reduce((acc: any, indicator) => {
        if (this.indicatorsWithNAStatusForEachVessel.includes(indicator.featureName)) return acc;
        acc[indicator.featureName.replace(/\s/g, "")] = {
          value: indicator.outageState,
          lastDataTimestamp: indicator.lastDataTimestamp,
          outageStartTime: indicator.outageStartTime,
          outageDetectionTime: indicator.outageDetectionTime,
        };
        return acc;
      }, {});
      return {
        vesselName: {
          value: item.vesselName ?? "Unknown Vessel",
          link: `/Vessel/${item.vesselId}/${encodeURIComponent(item.vesselName)}`,
        },
        overallStatus: {
          value: item.overallOutageState,
        },
        ...indicators,
      };
    });
    return Boolean(this.searchQuery !== null && this.searchQuery.trim())
      ? dataAvailability.filter(item => item.vesselName.value.toLowerCase().includes(this.searchQuery.toLowerCase()))
      : dataAvailability;
  }

  get tableLoading(): boolean {
    return DataQuality.loadingState;
  }

  @Watch("isSkeletonLoading")
  onSkeletonLoadingChange(newVal: boolean) {
    if (newVal) {
      if (this.skeletonTimeout) {
        clearTimeout(this.skeletonTimeout);
      }
      this.skeletonTimeout = window.setTimeout(() => {
        this.isSkeletonLoading = false;
      }, 4000);
    }
  }


  getFormatedDateTimeString(timestamp: string): string {
    return dateHelper.getFormatedDateTimeString(timestamp);
  }

  updateOldestDataTimestamp(vesselName: string, featureName: string, dataQualityRow: any): void {
    // Debounce updates to prevent multiple rapid refreshes
    if (this.updateDebounceTimer) {
      clearTimeout(this.updateDebounceTimer);
    }

    this.updateDebounceTimer = setTimeout(() => {
      DataQuality.dataAvailability.forEach(item => {
        if (item.vesselName === vesselName) {
          item.data.forEach(indicator => {
            if (indicator.featureName === featureName) {
              indicator.outageDetectionTime = dataQualityRow.outageDetectionTime;
              indicator.lastDataTimestamp = dataQualityRow.lastDataTimestamp;
              indicator.outageStartTime = dataQualityRow.outageStartTime;
              indicator.outageState = dataQualityRow.outageState;
            }
          });

          item.overallOutageState = item.data.reduce((acc, indicator) => {
            if (indicator.outageState > acc) {
              return indicator.outageState;
            }
            return acc;
          }, 0);
        }
      });

      const updatedOldestTimeStamp = this.oldestDataTimestamp();
      if (updatedOldestTimeStamp) {
        this.lastUpdatedTimestamp = updatedOldestTimeStamp;
      }
    }, 300);
  }

  async onFeaturesSaved(): Promise<void> {
    await DataQuality.refreshDataAvailability();
    this.buildHeaders();
  }

  buildHeaders(): void {
    const dataIndicatorHeaders = DataQuality.dataAvailability[0]?.data
      .map(indicator => ({
        text: indicator.featureName,
        type: "dataIndicator",
        value: indicator.featureName.replace(/\s/g, ""),
        width: "140px",
        align: "center",
        sortable: false,
      }))
      .filter(indicator => !this.indicatorsWithNAStatusForEachVessel.includes(indicator.text)) ?? [];
    const settingsHeader = { text: "", type: "settings", value: "settings", width: "60px", sortable: false, align: "center" };
    this.headers = [...this.baseHeaders, ...dataIndicatorHeaders, settingsHeader];
  }

  closeModal(): void {
    this.dialog = false;
  }
}
